import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { answerSurvey, claimSurvey, getSurveyStatus, type SurveyClaim } from '../api/survey';
import { useAuth } from '../auth/AuthProvider';
import { isGuestUser } from '../auth/guest';
import { getOverlayDepth } from '../commands/overlayStack';
import { useT } from '../i18n';
import { APP_VERSION } from '../native/appVersion';
import { isNativeCallModeActive } from '../native/liveCall';
import { getPurchaseStatus } from '../native/iap';
import { usePlatform } from '../platform/platform';
import { onReviewActivity } from '../app/reviewActivity';
import { useStartupGate } from '../app/startupGateContext';
import { SurveyCard } from './SurveyCard';

export function surveyMomentSafe(input: {
  turns: number; pending: number; sessionMs: number; settledMs: number;
  pathname: string; gate: string; blocked: boolean;
}): boolean {
  return input.turns >= 2 && input.pending === 0 && input.sessionMs >= 120_000 && input.settledMs >= 6_000
    && surveyRoute(input.pathname) && input.gate === 'none' && !input.blocked;
}

// Die Einladung wartet auf die Rückkehr zur Übersicht; im Gespräch liest und spielt man.
function surveyRoute(pathname: string): boolean {
  return /^\/(?:chats|dashboard|campaigns)?\/?$/.test(pathname);
}

function blockedNow(): boolean {
  return document.visibilityState !== 'visible' || getOverlayDepth() > 0 || isNativeCallModeActive()
    || !['idle', 'verified'].includes(getPurchaseStatus().state)
    || document.querySelector('[role="dialog"], [role="alertdialog"], [aria-modal="true"], dialog[open], [role="alert"]:not([data-survey-error]), [aria-busy="true"], .lorath-tour-backdrop') !== null
    || Array.from(document.querySelectorAll<HTMLElement>('[data-lorath-dock]:not([data-lorath-dock="survey"])')).some(el => !el.hidden)
    || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]') === true
    || Array.from(document.querySelectorAll<HTMLMediaElement>('audio, video')).some(el => !el.paused)
    || Boolean(window.speechSynthesis?.speaking);
}

export function SurveyHost() {
  const auth = useAuth();
  const location = useLocation();
  const gate = useStartupGate();
  const { lang } = useT();
  const platform = usePlatform();
  const userId = auth.user?.userId;
  const enabled = auth.status === 'authed' && !!userId && !isGuestUser(auth.user ?? null);
  const live = useRef({ pathname: location.pathname, gate });
  live.current = { pathname: location.pathname, gate };
  const [claim, setClaim] = useState<(SurveyClaim & { userId: number }) | null>(null);
  const [visible, setVisible] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setClaim(null); setVisible(false); setDone(false);
    if (!enabled || !userId) return;
    let cancelled = false;
    let claimed = false;
    let running = false;
    let final = false;
    let nextCheck = 0;
    let started = Date.now();
    let hiddenAt: number | null = null;
    let settledAt = started;
    let turns = 0;
    const pending = new Set<object>();
    const safe = () => surveyMomentSafe({ turns, pending: pending.size, sessionMs: Date.now() - started,
      settledMs: Date.now() - settledAt, ...live.current, blocked: blockedNow() });
    const tick = async () => {
      if (cancelled) return;
      const quiet = safe();
      setVisible(quiet);
      if (!quiet || claimed || running || final || Date.now() < nextCheck) return;
      running = true;
      nextCheck = Date.now() + 60_000;
      try {
        const status = await getSurveyStatus();
        if (cancelled) return;
        final = status.final;
        if (!status.eligible || !safe()) return;
        const result = await claimSurvey();
        if (cancelled) return;
        claimed = result.claimed;
        if (result.claimed) {
          setClaim({ ...result, userId });
          setVisible(safe());
        }
      } catch { /* Bei Netzfehlern bleibt die Einladung ruhig und wird später neu geprüft. */ }
      finally { running = false; }
    };
    const unsubscribe = onReviewActivity(event => {
      settledAt = Date.now();
      if (event.phase === 'start') pending.add(event.token);
      else if (pending.delete(event.token) && event.successful) turns += 1;
    });
    const onInput = () => { settledAt = Date.now(); };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') hiddenAt = Date.now();
      else if (hiddenAt !== null) {
        if (Date.now() - hiddenAt >= 30 * 60_000) { started = Date.now(); turns = 0; pending.clear(); }
        hiddenAt = null;
      }
      settledAt = Date.now();
    };
    const timer = window.setInterval(() => void tick(), 4_000);
    const observer = new MutationObserver(() => { if (!cancelled) setVisible(safe()); });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('input', onInput, true);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true; unsubscribe(); window.clearInterval(timer); observer.disconnect();
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, userId]);

  if (!enabled || done || !claim?.claimed || claim.userId !== userId) return null;
  return <SurveyCard key={userId} canSnooze={claim.canSnooze === true} visible={visible && surveyRoute(location.pathname) && gate === 'none'}
    suspended={!surveyRoute(location.pathname) || gate !== 'none'}
    onDone={() => setDone(true)} onAnswer={async (action, answers) => {
      const result = await answerSurvey(action, answers, { language: lang, app_version: APP_VERSION, platform: platform.isNative ? platform.os : 'web' });
      if (!result.saved) throw new Error('Umfrage nicht gespeichert');
    }} />;
}
